import {flushSync} from 'react-dom';
import {useCallback, useRef} from 'react';

import {animate, AnimatePresence} from '@sail/engine';
import {external} from '@sail/icons/react/Icon';
import {Icon, view} from '@sail/ui';
import type {IconAsset} from '@sail/icons/types';

import type {Conversation} from '@stripe-internal/conversations-client';
import {useAnalytics, useTheme} from '@stripe-internal/conversations-ui';

import {dotComColors} from '../../theme/dotCom';

type Option = {
  value: string;
  label: string;
  onPress?: () => void;
  icon?: IconAsset;
  externalUrl?: string;
};

type Props = {
  recommended?: boolean;
  selected?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  animated?: boolean;
  option: Option;
  onPress?: () => void;
  index: number;
  conversation?: Conversation;
};

// Keep up to date with Opus::SupportConversations::Utilities::Icons.
const recognizedLeadingEmoji = ['📞', '💬', '📧', '➢'];

const getOptionLabel = (option: Option) => {
  if (option.icon) {
    return option.label;
  }

  const emoji = recognizedLeadingEmoji.find((candidate) =>
    option.label.startsWith(candidate),
  );

  return emoji ? option.label.slice(emoji.length).trim() : option.label;
};

const ChatButtonOptionDotCom = ({
  selected = false,
  disabled = false,
  option,
  onPress,
  index,
  conversation,
}: Omit<Props, 'animated' | 'hidden' | 'recommended'>): JSX.Element => {
  const ref = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const analytics = useAnalytics();

  const label = getOptionLabel(option);

  const handleExternalLinkClick = useCallback(() => {
    const url = option.externalUrl;
    if (!url) {
      return;
    }
    analytics.action('presented_button_clicked', {
      buttonUrl: url,
      buttonTitle: label,
    });
    if (conversation) {
      conversation.action({
        name: 'button-clicked',
        data: {title: label, url},
      });
    }
  }, [analytics, conversation, label, option.externalUrl]);

  const handleClick = async () => {
    if (!ref.current || !ref.current.parentElement) {
      return;
    }

    const easing: KeyframeAnimationOptions = {
      duration: 350,
      easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
      fill: 'forwards',
    };
    const {parentElement} = ref.current;
    const selectedBtnHeight = ref.current.getBoundingClientRect().height;

    const collapseHeightAnimation = parentElement.animate(
      [
        {height: `${parentElement.offsetHeight}px`},
        {height: `${selectedBtnHeight}px`},
      ],
      easing,
    );

    const allButtons = parentElement.querySelectorAll('.chat-button-option');
    const nonSelectedButtonsFadeAnimation = Array.from(allButtons)
      .filter((buttonToFade) => buttonToFade !== ref.current)
      .map((buttonToFade) => {
        return buttonToFade.animate(
          [
            {
              opacity: 1,
              transform: 'translateY(0px)',
            },
            {
              opacity: 0,
              transform: `translateY(-${selectedBtnHeight}px)`,
            },
          ],
          easing,
        ).finished;
      });

    const selectedBtnPositionAnimation = ref.current.animate(
      [
        {
          transform: 'translateY(0px)',
        },
        {
          transform: `translateY(-${ref.current.offsetTop}px)`,
          borderColor: theme.linkColor,
          background: theme.linkColor,
          color: dotComColors.brand,
          zIndex: 10,
        },
      ],
      easing,
    );

    const animations = await Promise.all([
      collapseHeightAnimation.finished,
      selectedBtnPositionAnimation.finished,
      ...nonSelectedButtonsFadeAnimation,
    ]);
    animations.forEach((animation) => animation.cancel());

    // Ensure React observes selection immediately after every click animation.
    flushSync(() => onPress && onPress());
  };

  const buttonCss = {
    transformOrigin: 'right bottom',
    border: '1px solid',
    borderColor: selected ? theme.linkColor : dotComColors.brand300,
    borderRadius: 'medium',
    background: selected ? theme.linkColor : dotComColors.brand,
    paddingX: '14px',
    paddingY: 'small',
    whiteSpace: 'pre-line',
    overflow: 'hidden',
    overflowWrap: 'break-word',
    color: selected ? dotComColors.brand : dotComColors.brand600,
    width: 'fit-content',
    maxHeight: 'fit-content',
    textAlign: 'start',
    ...(!selected && {
      ':hover': {
        color: theme.linkColor,
        borderColor: '#c4ccd8',
        boxShadow:
          '0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.06), 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.1)',
      },
    }),
  } as const;

  const buttonUses = [
    animate.in({
      duration: 250,
      opacity: 0,
      delay: index * 100,
      transform: 'translateX(20px)',
      easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
      disabled: selected,
    }),
  ];

  if (option.externalUrl) {
    return (
      <view.a
        href={option.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        // `data-text` is used across the universal help synthetic test suite.
        data-text={label}
        className="chat-button-option"
        data-testid="chat-option"
        css={{...buttonCss, textDecoration: 'none'}}
        onClick={handleExternalLinkClick}
        uses={buttonUses}
      >
        {label}
        <Icon icon={external} size="xsmall" css={{marginLeft: 'small'}} />
      </view.a>
    );
  }

  return (
    <view.button
      ref={ref}
      // `data-text` is used across the universal help synthetic test suite.
      data-text={label}
      className={`chat-button-option${selected ? ' selected' : ''}`}
      data-testid="chat-option"
      disabled={disabled}
      css={buttonCss}
      onClick={handleClick}
      uses={buttonUses}
    >
      {label}
    </view.button>
  );
};

export const ChatButtonOption = ({
  hidden = false,
  ...props
}: Props): JSX.Element => {
  return (
    <AnimatePresence>
      {hidden ? null : <ChatButtonOptionDotCom {...props} />}
    </AnimatePresence>
  );
};
